"use client";
import { iconList } from "@/assets/icons";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import { StepNumberWrap } from "@/components/atoms/step-number-wrap";
import React, { useState } from "react";

interface ShareStepsBlockProps {
  shareSteps?: {
    title: string | null | undefined;
    expand_content: string | null | undefined;
    collapse_content: string | null | undefined;
    content: string | null | undefined;
  }[];
}
/**
 * ShareStepsBlock component
 * Displays a step-by-step expandable section.
 * Each step can toggle between expanded/collapsed states to show additional content.
 */
const ShareStepsBlock: React.FC<ShareStepsBlockProps> = ({ shareSteps }) => {
  const { AngleDownSmall, AngleRightSmall } = iconList;

  // Initialize state where only the first step is expanded by default
  const [expandedSteps, setExpandedSteps] = useState<boolean[] | undefined>(
    shareSteps?.map((_, index) => index === 0),
  );

  // Toggles visibility of a specific step content
  const toggleStep = (index: number) => {
    setExpandedSteps((prev) =>
      prev?.map((val, i) => (i === index ? !val : val)),
    );
  };
  return (
    <div className="xs-390:min-h-[1050px] xs-450:min-h-[811px] xs-320:min-h-[1113px] min-h-[1310px] space-y-6 max-[345px]:min-h-[1226px] sm:min-h-[720px]">
      {shareSteps?.map((step, index) => {
        const isExpanded = expandedSteps?.[index];

        return (
          <div
            key={step.title}
            className="xs-390:first:min-h-[829px] xs-414:first:min-h-[689px] xs-450:first:min-h-[547px] flex gap-4 first:min-h-[892px] sm:first:min-h-[584px] md:first:min-h-[540px]"
          >
            <StepNumberWrap>{index + 1}</StepNumberWrap>
            <div className="flex-1">
              <div className="flex flex-wrap items-center">
                <h3
                  className="py-2 text-lg"
                  style={{ color: "var(--text-gray-light)" }}
                >
                  {step.title}
                </h3>
                {step.content && (
                  <button
                    onClick={() => toggleStep(index)}
                    className="inline-flex cursor-pointer items-center text-lg"
                    style={{
                      borderBottom: "1px solid var(--text-gray-light)",
                      color: "var(--text-gray-light)",
                    }}
                  >
                    {isExpanded ? step.collapse_content : step.expand_content}
                    {isExpanded ? <AngleDownSmall /> : <AngleRightSmall />}
                  </button>
                )}
              </div>

              {/* Collapsible content area with smooth transition */}
              <div
                className={`transition-max-height overflow-hidden duration-800 ease-in-out ${
                  isExpanded ? "max-h-[2000px]" : "max-h-0"
                }`}
              >
                <RichtextRenderer
                  customStyle={{
                    "": {
                      fontSize: "14px",
                      paddingTop: "15px",
                    },
                  }}
                  content={step?.content || ""}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ShareStepsBlock;
